import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { MapPin, CheckCircle, Timer, Phone } from "lucide-react";

// Lightweight toast UI (used inline)
const Toast = ({ message, onClose }) => (
  <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded shadow-lg z-50">
    <div className="flex items-center gap-3">
      <div className="flex-1 text-sm">{message}</div>
      <button
        onClick={onClose}
        className="text-sm underline opacity-90 hover:opacity-100"
      >
        Dismiss
      </button>
    </div>
  </div>
);

const DriverInterface = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const prevDeliveriesRef = useRef([]); // keep previous list
  const initialLoadRef = useRef(true); // avoid notifying on first fetch
  const audioRef = useRef(null);
  const mountedRef = useRef(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // initialize audio and request notification permission
  useEffect(() => {
    mountedRef.current = true;

    // Create audio element (put ding.mp3 in public/)
    try {
      audioRef.current = new Audio("/ding.mp3");
      audioRef.current.preload = "auto";
    } catch (e) {
      console.warn("Audio init failed:", e);
      audioRef.current = null;
    }

    // Request Notification permission (best-effort)
    if ("Notification" in window && Notification.permission !== "granted") {
      try {
        Notification.requestPermission().then((perm) => {
          console.log("Notification permission:", perm);
        });
      } catch (e) {
        // Ignore if blocked
      }
    }

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // notify helper: toast + system notification + sound
  const notifyDriver = (message) => {
    if (!mountedRef.current) return;

    console.log("Notify driver:", message);
    setToast(message);

    // auto-hide after 5s
    setTimeout(() => {
      setToast("");
    }, 5000);

    // system notification if allowed
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("New Delivery Assigned", { body: message });
      } catch (e) {
        console.warn("System notification failed:", e);
      }
    }

    // try to play sound (may be blocked until user interacts)
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .catch((err) => {
          // autoplay blocked until user interaction — that's normal in many browsers
          console.warn("Audio play prevented (need user interaction):", err);
        });
    }
  };

  // Fetch deliveries assigned to driver
  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/driver/deliveries`, {
        withCredentials: true,
      });

      const list = Array.isArray(res.data) ? res.data : res.data?.deliveries || [];

      // detect newly assigned deliveries (IDs present in list but not prev)
      if (!initialLoadRef.current) {
        const prevIds = new Set(prevDeliveriesRef.current.map((d) => d._id));
        const newOnes = list.filter((d) => !prevIds.has(d._id));
        if (newOnes.length > 0) {
          // notify about the first new one (you can loop or show count)
          notifyDriver(`📦 New delivery: #${newOnes[0].orderNumber}`);
        }
      } else {
        // first successful fetch — do not notify
        initialLoadRef.current = false;
      }

      prevDeliveriesRef.current = list;
      setDeliveries(list);
    } catch (err) {
      console.error("Failed to fetch deliveries:", err);
      setError(err?.response?.data?.message || "Failed to load deliveries.");
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  // polling
  useEffect(() => {
    // initial fetch
    fetchDeliveries();

    const interval = setInterval(fetchDeliveries, 60000);
    return () => clearInterval(interval);
  }, [fetchDeliveries]);

  // Update status (Processing → In Transit → Delivered/Failed)
  const updateStatus = async (delivery_id, newStatus) => {
    try {
      await axios.put(
        `${BASE_URL}/api/driver/deliveries/${delivery_id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );

      if (newStatus === "Delivered" || newStatus === "Failed Delivery") {
        // remove only completed/failed deliveries
        setDeliveries((prev) => prev.filter((d) => d._id !== delivery_id));
      } else {
        // update status in-place for ongoing deliveries
        setDeliveries((prev) =>
          prev.map((d) => (d._id === delivery_id ? { ...d, status: newStatus } : d))
        );
      }
    } catch (err) {
      console.error(err);
      setError("Failed to update status.");
    }
  };

  // status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-gray-200 text-gray-700";
      case "Processing":
        return "bg-yellow-100 text-yellow-700";
      case "In Transit":
        return "bg-blue-100 text-blue-700";
      case "Delivered":
        return "bg-green-100 text-green-700";
      case "Failed Delivery":
        return "bg-red-100 text-red-700";
      case "Cancelled":
        return "bg-red-200 text-red-800";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {toast && <Toast message={toast} onClose={() => setToast("")} />}

      <h1 className="text-2xl font-bold mb-6">Driver Deliveries</h1>

      {loading && <p className="mb-4">Loading deliveries...</p>}
      {error && <p className="text-red-600 bg-red-100 p-3 rounded mb-4">{error}</p>}

      {deliveries.length === 0 && !loading ? (
        <p>No deliveries assigned.</p>
      ) : (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          {deliveries.map((delivery) => (
            <div key={delivery._id} className="border rounded-lg p-4 shadow-sm bg-white flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-3 min-w-0">
                <h2 className="text-lg font-semibold truncate" title={`Order #${delivery.orderNumber}`}>
                  #{delivery.orderNumber}
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ${getStatusColor(delivery.status)}`}>
                  {delivery.status}
                </span>
              </div>

              {/* Delivery details */}
              <div className="space-y-1 text-sm">
                <p><strong>Customer Name:</strong> {delivery.customerName}</p>
                <p><strong>Customer Email:</strong> {delivery.customerEmail}</p>
                <p><strong>Address:</strong> {delivery.address}</p>
                <p><strong>City:</strong> {delivery.deliveryCity}</p>
                <p><strong>Phone:</strong> {delivery.customerPhone}</p>
                <p><strong>Delivery Instruction:</strong> {delivery.deliveryInstruction}</p>
              </div>

              {/* Action buttons */}
              <div className={`mt-4 gap-2 ${
                window.innerWidth <= 768 ? "flex flex-col"
                : delivery.status === "Processing" ? "flex flex-col sm:flex-row sm:flex-wrap"
                : delivery.status === "In Transit" ? "grid sm:grid-cols-2"
                : "flex flex-col sm:flex-row sm:flex-wrap"
              }`}>
                {/* Always show maps link */}
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.address)}`} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1">
                  <button className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm">
                    <MapPin className="w-4 h-4" />
                    Open in Maps
                  </button>
                </a>

                {/* Call button */}
                {(delivery.status === "Processing" || delivery.status === "In Transit") && (
                  <a href={`tel:${delivery.customerPhone}`} className="w-full sm:flex-1">
                    <button className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm">
                      <Phone className="w-4 h-4" />
                      Call Customer
                    </button>
                  </a>
                )}

                {/* Processing → In Transit */}
                {delivery.status === "Processing" && (
                  <button onClick={() => updateStatus(delivery._id, "In Transit")} className="w-full sm:flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                    <Timer className="w-4 h-4" />
                    Start Delivery
                  </button>
                )}

                {/* In Transit → Delivered / Failed */}
                {delivery.status === "In Transit" && (
                  <>
                    <button onClick={() => updateStatus(delivery._id, "Delivered")} className="w-full sm:flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Mark Delivered
                    </button>

                    <button onClick={() => updateStatus(delivery._id, "Failed Delivery")} className="w-full sm:flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm">
                      🚫 Report Failed
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DriverInterface;
